import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Home, 
  Building2, 
  TreePine, 
  CheckCircle2,
  Package
} from 'lucide-react';
import { useState } from 'react';
import DIYOrderModal from './DIYOrderModal';

const PricingSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const pricingOptions = [
    {
      icon: <Home className="w-12 h-12 text-blue-400" />,
      title: 'Residential Permanent Lighting',
      price: '$31',
      unit: 'per foot',
      description: 'Professional installation of IllumiDEL permanent lighting system for your home',
      features: [
        '20-Year Warranty',
        'Smart App Control',
        'Unlimited Color Options',
        'Professional Installation',
        'Year-Round Use'
      ]
    },
    {
      icon: <Building2 className="w-12 h-12 text-blue-400" />,
      title: 'Commercial Permanent Lighting',
      price: '$31',
      unit: 'per foot',
      additionalCost: '+ lift costs',
      description: 'Enhance your business with professional permanent lighting solutions',
      features: [
        'Same Quality as Residential',
        'High-Output Options',
        'Brand Enhancement',
        'Professional Installation',
        'Custom Business Solutions'
      ]
    },
    {
      icon: <TreePine className="w-12 h-12 text-blue-400" />,
      title: 'Tree Wrapping',
      pricing: [
        { height: '20ft tree', price: '$2,000' },
        { height: '60ft tree', price: '$5,500' }
      ],
      description: 'Professional tree wrapping services for stunning landscape lighting',
      includes: [
        'Design',
        'Professional grade lights and wires',
        'Installation & takedown'
      ],
      features: [
        'Up to 60ft Tall',
        'Weather Resistant'
      ]
    },
    {
      icon: <Package className="w-12 h-12 text-blue-400" />,
      title: 'DIY Hardware Kits',
      price: 'Custom',
      unit: 'pricing',
      description: 'Purchase professional-grade materials and install it yourself',
      isDIY: true,
      features: [
        '6ft Aluminum Tracks',
        'IllumiDEL Professional Lights',
        'Colour Matched Screws',
        'Installation Accessories',
        'Professional Quality Materials'
      ]
    }
  ];

  const scrollToQuote = () => {
    document.getElementById('quote').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pricing" className="py-24 bg-gray-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* 15% Off Banner */}
        <div className="mb-12 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 shadow-2xl border-2 border-blue-400 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
            <div className="relative z-10 text-center">
              <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-1 mb-3">
                <span className="text-white font-semibold text-sm uppercase tracking-wide">Limited Time Offer</span>
              </div>
              <h3 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                Save 15% When You Request Online!
              </h3>
              <p className="text-white/90 text-lg">
                Get your free quote through our website and receive an exclusive 15% discount on your project
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Transparent Pricing</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Professional lighting solutions with straightforward pricing. No hidden fees.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {pricingOptions.map((option, index) => (
            <Card key={index} className="border-2 border-blue-400/20 bg-gray-800 hover:border-blue-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-400/20">
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-4">
                  <div className="bg-blue-500/10 p-4 rounded-full">
                    {option.icon}
                  </div>
                </div>
                <CardTitle className="text-2xl text-white mb-2">{option.title}</CardTitle>
                
                {/* Pricing Display */}
                <div className="mt-4">
                  {option.pricing ? (
                    <div className="space-y-2">
                      {option.pricing.map((item, idx) => (
                        <div key={idx} className="bg-gray-700/50 rounded-lg p-3">
                          <p className="text-gray-300 text-sm">{item.height}</p>
                          <p className="text-3xl font-bold text-blue-400">{item.price}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-4xl font-bold text-blue-400">{option.price}</span>
                        <span className="text-gray-300">{option.unit}</span>
                      </div>
                      {option.additionalCost && (
                        <p className="text-gray-400 text-sm mt-2">{option.additionalCost}</p>
                      )}
                    </div>
                  )}
                </div>
                
                <p className="text-gray-300 mt-4">{option.description}</p>
              </CardHeader>
              
              <CardContent>
                {/* This Includes Section for Tree Wrapping */}
                {option.includes && (
                  <div className="mb-6 bg-gray-700/30 rounded-lg p-4">
                    <p className="text-blue-400 font-semibold mb-3 text-center">This Includes:</p>
                    <ul className="space-y-2">
                      {option.includes.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-gray-200">
                          <CheckCircle2 className="text-blue-400 flex-shrink-0" size={18} />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <ul className="space-y-3 mb-6">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-200">
                      <CheckCircle2 className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={option.isDIY ? () => setIsModalOpen(true) : scrollToQuote}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-6"
                >
                  {option.isDIY ? 'Order Kits' : 'Get Your Free Quote'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-300 text-lg mb-4">
            *All pricing includes professional installation and materials. Final quote may vary based on project specifics.
          </p>
          <p className="text-gray-400">
            Contact us for a free, no-obligation quote tailored to your property.
          </p>
        </div>
      </div>

      {/* DIY Order Modal */}
      <DIYOrderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};

export default PricingSection;
